/* eslint-disable @typescript-eslint/no-explicit-any */
import { Icon } from '@pelando/components';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { ChevronDown, Ghost } from '@pelando/fontawesome/icons';
import { RemoveVoteVariables } from '../../../../infra/api/votes/types/RemoveVote';
import { VoteVariables } from '../../../../infra/api/votes/types/Vote';
import {
  StorePromotions,
  StorePromotionsQueryVariables,
} from '../../../../infra/api/stores/types/IstorePromotionsQueryDTO';

import { getAddOfferUrl } from '../../../services/urls';
import StoreDealsTabSkeleton from './skeleton';
import { StoreDealsList } from './StoreDealList';
import {
  DealsEmptyState,
  DealStoreSeeMore,
  StoreDealsTabContainer,
} from './style';
import { VoteWithTemperature } from '../../../../infra/api/votes/types/UserOfferVotes';
import { useTranslation } from '../../../hooks/useTranslation';

export type StoreDealsTabContentProps = {
  isLoading?: boolean;
  storeId: string;
  storeDeals?: StorePromotions;
  userOffersVote?: Record<string, VoteWithTemperature>;
  getStorePromotions: (variables: StorePromotionsQueryVariables) => void;
  onVote: (variables: VoteVariables) => void;
  onRemoveVote: (variables: RemoveVoteVariables) => void;
};

export const STORE_DEALS_PAGINATION_LIMIT = 12;

const StoreDealsTabContent = ({
  storeId,
  storeDeals,
  userOffersVote,
  isLoading,
  onRemoveVote,
  onVote,
  getStorePromotions,
}: StoreDealsTabContentProps) => {
  const { t } = useTranslation('coupons');
  const router = useRouter();

  const deals = useMemo(() => storeDeals?.edges || [], [storeDeals]);

  const haveDeals = deals.length > 0;

  const hasNextPage = storeDeals?.pageInfo?.hasNextPage;
  const endCursor = storeDeals?.pageInfo?.endCursor;
  const shouldShowExpandButton = haveDeals && hasNextPage && !isLoading;

  const handleFetchMore = () => {
    getStorePromotions({
      storeId,
      after: endCursor,
      limit: STORE_DEALS_PAGINATION_LIMIT,
    });
  };

  if (!haveDeals && !isLoading) {
    const emptyStateProps = {
      icon: <Icon icon={Ghost} />,
      title: t('empty-state-promotion-title'),
      buttonTitle: t('empty-state-promotion-button'),
      content: t('empty-state-promotion-content'),
      onClick: () => router.push(getAddOfferUrl()),
    };

    return <DealsEmptyState {...emptyStateProps} />;
  }

  return (
    <StoreDealsTabContainer>
      <StoreDealsList
        deals={deals}
        userOffersVote={userOffersVote}
        onVote={onVote}
        onRemoveVote={onRemoveVote}
      />
      {isLoading && (
        <StoreDealsTabSkeleton
          cardsLength={STORE_DEALS_PAGINATION_LIMIT}
          animate
        />
      )}
      {shouldShowExpandButton && (
        <DealStoreSeeMore type="button" onClick={handleFetchMore}>
          {t('deal-store-see-more')}
          <Icon icon={ChevronDown} style={{ fontSize: 24 }} />
        </DealStoreSeeMore>
      )}
    </StoreDealsTabContainer>
  );
};

export default StoreDealsTabContent;
